/**
 * localStorage-backed handle to a pending Pong match the player can
 * reconnect into after a browser close, refresh, or network drop.
 *
 * Server (Colyseus) holds the seat for `graceSec` after a non-consented
 * disconnect (90 s on first drop, 30 s on second). We store the
 * reconnectionToken plus minimal display info so the floating
 * `<PongReconnectCard>` can render and the `/pong` page can call
 * `client.reconnect(token)` without the user re-queueing.
 *
 * Cleared on:
 *   - game_over arrival (match ended cleanly)
 *   - explicit forfeit (user chose to lose)
 *   - server reject of reconnect (grace expired)
 *   - explicit `clearPendingMatch()` from the UI
 */

const STORAGE_KEY = "hupper.pong.reconnect";

export interface PendingPongMatch {
  /** Colyseus roomId — the match identifier on the game-server. */
  matchId: string;
  /** Per-player stake in USDT (display only). Total pot is `stake * 2`. */
  stake: number;
  /** Opponent wallet for the card avatar. */
  opponentWallet: string | null;
  /** Token from `room.reconnectionToken`. */
  reconnectionToken: string;
  /** WebSocket URL used to join (so we reconnect to the right host). */
  gameServerUrl: string;
  /** Wallet of the player who owns this pending session. Used to gate
   *  the card by the currently-logged-in account. */
  walletAddress: string;
  /** Wall-clock time of `match_found`. */
  matchStartedAtMs: number;
  /** Wall-clock time of detected disconnect. Set in onLeave OR in the
   *  synchronous `pagehide` listener so we still capture it on browser
   *  close / refresh / mobile-tab-eviction. */
  disconnectedAtMs?: number;
  /**
   * Mirror of server's `dropsBySession[sid]`. Drives the grace ladder:
   *   1st drop → 90 s
   *   2nd drop → 30 s
   *   3rd+    → 0 s (instant forfeit; card shouldn't show)
   * Incremented on every detected disconnect, never decremented (server
   * doesn't decrement either — a successful reconnect doesn't reset).
   */
  dropCount: number;
  /** Conservative absolute upper bound for "this match might still be
   *  alive". Past this, we give up trying to reconnect. */
  expiresAtMs: number;
}

/**
 * Server's drop-grace ladder mirror. MUST stay in sync with
 * `services/game-server/src/rooms/PongArena.ts:onLeave`.
 */
export function graceSecForDrop(dropCount: number): number {
  if (dropCount === 1) return 90;
  if (dropCount === 2) return 30;
  return 0;
}

/** Hard cap from match start beyond which we never attempt reconnect. */
const MAX_MATCH_DURATION_MS = 10 * 60 * 1000;

export function savePendingMatch(
  m: Omit<PendingPongMatch, "expiresAtMs" | "dropCount"> & {
    expiresAtMs?: number;
    dropCount?: number;
  },
): void {
  if (typeof window === "undefined") return;
  const expiresAtMs = m.expiresAtMs ?? m.matchStartedAtMs + MAX_MATCH_DURATION_MS;
  const payload: PendingPongMatch = {
    ...m,
    expiresAtMs,
    dropCount: m.dropCount ?? 0,
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage might be disabled — non-fatal, reconnect won't be offered */
  }
}

/**
 * Bump the drop counter and stamp the disconnect time atomically.
 * Called from both `room.onLeave` (async, runs after socket close) and
 * the synchronous `pagehide` listener (runs on browser close before the
 * page is torn down). Idempotent if both fire — `disconnectedAtMs` is
 * only set if not already set this drop, and dropCount is gated by a
 * 5-second window so a bouncing socket doesn't double-count.
 */
export function recordDisconnect(): void {
  const cur = loadPendingMatch();
  if (!cur) return;
  const now = Date.now();
  // If we already stamped a disconnect within the last 5 s, this is a
  // duplicate event for the same drop — only update timestamp, don't
  // re-increment the count.
  const isDuplicate = cur.disconnectedAtMs !== undefined
    && now - cur.disconnectedAtMs < 5_000;
  savePendingMatch({
    ...cur,
    disconnectedAtMs: cur.disconnectedAtMs ?? now,
    dropCount: isDuplicate ? cur.dropCount : cur.dropCount + 1,
  });
}

/** Reset disconnect markers after a successful reconnect. */
export function clearDisconnectMark(): void {
  const cur = loadPendingMatch();
  if (!cur) return;
  if (cur.disconnectedAtMs === undefined) return;
  savePendingMatch({ ...cur, disconnectedAtMs: undefined });
}

export function loadPendingMatch(): PendingPongMatch | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingPongMatch;
    if (!parsed?.matchId || !parsed?.reconnectionToken) return null;
    if (Date.now() > parsed.expiresAtMs) {
      clearPendingMatch();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingMatch(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Compute the seconds remaining in the reconnect grace window using
 * the server's per-drop ladder. Returns 0 if expired or if the next
 * drop would be an instant forfeit (3rd+).
 *
 * If `disconnectedAtMs` was never stamped (rare — pagehide + onLeave
 * both missed) we don't know exactly when the timer started, so we
 * conservatively show the FULL grace window. The reconnect attempt
 * itself is the source of truth — server will reject if expired.
 */
export function reconnectSecondsLeft(m: PendingPongMatch): number {
  const grace = graceSecForDrop(m.dropCount);
  if (grace === 0) return 0;
  if (m.disconnectedAtMs === undefined) return grace;
  const elapsed = (Date.now() - m.disconnectedAtMs) / 1000;
  return Math.max(0, Math.ceil(grace - elapsed));
}

/** sessionStorage flag asking the /pong page to auto-reconnect on mount. */
const RECONNECT_INTENT_KEY = "hupper.pong.reconnect-intent";
/** In-page custom event so an already-mounted /pong page also reacts. */
export const RECONNECT_REQUEST_EVENT = "hupper:pong-reconnect-requested";

export function setReconnectIntent(): void {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.setItem(RECONNECT_INTENT_KEY, "1"); } catch { /* */ }
  // Also fire a same-tab event so a PongGame already mounted (idle on
  // /pong with a stale pending match) can react without remounting.
  try { window.dispatchEvent(new Event(RECONNECT_REQUEST_EVENT)); } catch { /* */ }
}

export function consumeReconnectIntent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const v = window.sessionStorage.getItem(RECONNECT_INTENT_KEY);
    if (v) window.sessionStorage.removeItem(RECONNECT_INTENT_KEY);
    return v === "1";
  } catch {
    return false;
  }
}
